import WSPromisify from 'wspromisify';
import { Command, Message } from 'src/api/command';
import {
  Statistics,
  Distribution,
  Postings,
  Boards,
  BatchActions,
  Tasks,
  Users,
} from 'src/api';
import { showServerErrorMessageNotify } from 'src/services/notify';
import { Types } from 'src/api/ws-handler';
import ErrorData = Types.ErrorMessageData;
import MessageError = Types.MessageError;

export class ApiClient {
  private wsp: WSPromisify;

  constructor(wsp: WSPromisify) {
    this.wsp = wsp;
  }

  setWSP(wsp: WSPromisify) {
    this.wsp = wsp;
  }

  execute<TData, TResult = []>(command: Command, data: TData) {
    const message = command.getMessage<TData>(data);
    return (
      this.wsp
        .send<Message<TData>, TResult>(message)
        // need to check is this actual data or error message from by sendErrorMessage()
        .then((data) => {
          if (isErrorMessage(data)) {
            throw new MessageError(data as unknown as ErrorData);
          }
          return data;
        })
        .catch((error) => {
          process.env.DEBUGGING && console.error('API EXCEPTION', error);
          const errorMessage = getErrorMessage(error as object);
          showServerErrorMessageNotify({
            message: `Request to {${message.service}/${message.action}} failed`,
            error: errorMessage,
          });
          throw new Error(errorMessage);
        })
    );
  }

  get batchActions() {
    return new BatchActions.Commands({
      service: BatchActions.SERVICE,
      client: this,
    });
  }

  get boards() {
    return new Boards.Commands({
      service: Boards.SERVICE,
      client: this,
    });
  }

  get distribution() {
    return new Distribution.Commands({
      service: Distribution.SERVICE,
      client: this,
    });
  }

  get postings() {
    return new Postings.Commands({
      service: Postings.SERVICE,
      client: this,
    });
  }

  get statistics() {
    return new Statistics.Commands({
      service: Statistics.SERVICE,
      client: this,
    });
  }

  get tasks() {
    return new Tasks.Commands({
      service: Tasks.SERVICE,
      client: this,
    });
  }

  get users() {
    return new Users.Commands({
      service: Users.SERVICE,
      client: this,
    });
  }
}

function isErrorMessage(data: { message?: string; error?: string }): boolean {
  return 'message' in data && 'error' in data;
}

function getErrorMessage(error: MessageError | Error | object): string {
  if (error instanceof MessageError) {
    return error.error;
  }

  const errKeys = Object.keys(error);
  if (errKeys.length && errKeys[0].includes('Websocket timeout expired')) {
    return 'Server not responded in time';
  }

  return JSON.stringify(error);
}
